
import React, { useMemo } from 'react';
import './sparkline-chart.css';

function SparklineChart({ 
  data = [], 
  width = 120, 
  height = 40, 
  strokeWidth = 2,
  color = '#48D6B4',
  className = ''
}) {
  
  // Generate SVG path from price data
  const svgPath = useMemo(() => {
    if (!data || data.length < 2) {
      return '';
    }

    // Extract numeric values
    const values = data.map(point => {
      if (Array.isArray(point)) {
        return parseFloat(point[1]); // [timestamp, price] format
      }
      return parseFloat(point); // Just price
    }).filter(val => !isNaN(val));

    if (values.length < 2) {
      return '';
    }

    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const range = maxValue - minValue;

    // Avoid division by zero for flat lines
    if (range === 0) {
      const y = height / 2;
      return `M 0 ${y} L ${width} ${y}`;
    }

    // Generate path points
    const pathPoints = values.map((value, index) => {
      const x = (index / (values.length - 1)) * width;
      const y = height - ((value - minValue) / range) * height;
      return `${x} ${y}`;
    });

    return `M ${pathPoints.join(' L ')}`;
  }, [data, width, height]);

  // Determine color based on trend
  const chartColor = useMemo(() => {
    if (!data || data.length < 2) return color;

    const firstValue = Array.isArray(data[0]) ? data[0][1] : data[0];
    const lastValue = Array.isArray(data[data.length - 1]) ? data[data.length - 1][1] : data[data.length - 1];

    if (lastValue > firstValue) {
      return '#48D6B4'; // Green for uptrend
    } else if (lastValue < firstValue) {
      return '#FF6B6B'; // Red for downtrend
    } else {
      return '#FF9250'; // Orange for flat
    }
  }, [data, color]);

  if (!data || data.length < 2) {
    return (
      <div className={`sparkline-chart no-data ${className}`} style={{ width, height }}>
        <div className="no-data-message">No data</div>
      </div>
    );
  }

  return (
    <div className={`sparkline-chart ${className}`} style={{ width, height }}>
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className="sparkline-svg"
      >
        <defs>
          <linearGradient id={`gradient-${Math.random()}`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={chartColor} stopOpacity="0.8" />
            <stop offset="100%" stopColor={chartColor} stopOpacity="0.4" />
          </linearGradient>
        </defs>
        
        <path
          d={svgPath}
          fill="none"
          stroke={chartColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="sparkline-path"
        />
        
        {/* Optional: Add fill area under the line */}
        {data.length > 2 && (
          <path
            d={`${svgPath} L ${width} ${height} L 0 ${height} Z`}
            fill={`url(#gradient-${Math.random()})`}
            className="sparkline-fill"
          />
        )}
      </svg>
      
      {/* Hover overlay for interaction */}
      <div className="sparkline-overlay"></div>
    </div>
  );
}

export default SparklineChart;
